/**
 * The vocabulary of the activity detail page.
 *
 * Split out when the task card became its own component: the two need the
 * same types, the same date formatting and the same file handling, and a
 * shared module is better than either importing from the other.
 */
import { Fragment, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAction } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import {
  Badge,
  Box,
  Button,
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRoot,
  FileUploadTrigger,
  HStack,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuFileDown, LuTrash2, LuUpload } from "react-icons/lu";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { readableError } from "@/lib/errors";
import { useEffectivePersonId } from "../impersonation";
import type { ReviewFieldValue } from "@/components/ReviewFormFields";
import type {
  ActivityFormFieldTypeValue,
  ActivityReviewDecisionValue,
  ActivityReviewModeValue,
  ActivityReviewRoleValue,
  ActivityTaskStatusValue,
  PersonActivityStatusValue,
  ReviewCapacityValue,
} from "@convex/schemaEnums";
import { toastError } from "../components/ui/toaster";
import { TextLink } from "../components/TextLink";
import { tagColor } from "@/lib/tagColor";

/** One task of an activity, as the detail query returns it. */
export type AssignmentTask = AssignmentDetail["tasks"][number];

export type ReviewerSpecValue =
  | { kind: "role"; role: ActivityReviewRoleValue }
  | { kind: "supervisor" }
  | { kind: "coSupervisor" }
  | { kind: "person"; personId: Id<"persons"> };

export type TaskDetailResponse = FunctionReturnType<typeof api.personActivities.getPersonActivityDetail>;
// Files live in Azure Blob and are referenced by `key` (see convex/azureBlob.ts).
export type BaseFileResponseValue = {
  key: string;
  fileName: string;
  mimeType: string | null;
  size: number | null;
};

export type FileResponseValue = BaseFileResponseValue & { url?: string | null };
// A `multiple` file field holds an array; a single file field holds one object.
export type FieldValue = string | number | boolean | FileResponseValue | FileResponseValue[] | null;

export type FieldDefinition = {
  key: string;
  label: string;
  description?: string;
  type: ActivityFormFieldTypeValue;
  required: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  multiple?: boolean;
};

export type AssignmentDetail = {
  _id: Id<"personActivities">;
  titleSnapshot: string;
  descriptionSnapshot: string;
  tagSnapshots: Array<{
    tagId: Id<"activityTags">;
    name: string;
    key: string;
    color: string | null;
  }>;
  required: boolean;
  status: PersonActivityStatusValue;
  statusLabel: string;
  // Why it is blocked, where a dependency rather than a date is the cause.
  blockedBy: Array<{ title: string; personActivityId: string | null }>;
  allStepsDone: boolean;
  dueDay: string | null;
  recommendedStartDay: string | null;
  opensOnDay: string | null;
  person: {
    firstName: string;
    lastName: string;
  } | null;
  // Derived from the query rather than described, unlike the fields around it
  // -- which is the direction this whole type is meant to go.
  history: NonNullable<TaskDetailResponse>["history"];
  subject: NonNullable<TaskDetailResponse>["subject"];
  tasks: Array<{
    _id: Id<"personActivityTasks">;
    titleSnapshot: string;
    descriptionSnapshot: string;
    required: boolean;
    // Who fills it in, and whether that is somebody other than the person
    // the activity belongs to. Resolved on the server, because "the
    // supervisor" is a different person for every assignee.
    // What a reviewer answers when signing. Snapshotted with the task, so
    // reading a past review back never depends on the catalogue.
    reviewFormSchema: { fields: FieldDefinition[] } | null;
    // The step's own deadline, where it has one, already materialised.
    dueDay: string | null;
    // Present when the step is completed by a RECORD rather than by work.
    // Absent on nearly every task.
    participationCriteria?: {
      roles: string[];
      seriesId: Id<"eventSeries"> | null;
      eventId: Id<"events"> | null;
      externalKind: string | null;
    };
    // The record that closed it, in enough detail for a reviewer to judge
    // it rather than merely recognise it. Null while it is outstanding.
    citedRecord: {
      participationId: Id<"participations">;
      role: string;
      state: string;
      startDay: string;
      endDay: string | null;
      kind: string | null;
      organiser: string | null;
      location: string | null;
      url: string | null;
      credits: number | null;
      grade: string | null;
      note: string;
      recordedBySelf: boolean;
      title: string;
    } | null;
    actorLabel: string;
    performedByOther: boolean;
    // Whether this viewer may see the answers. False on somebody else's
    // unsubmitted draft, in either direction.
    contentVisible: boolean;
    viewerCanPerform: boolean;
    viewerCanWithdraw: boolean;
    actor:
      | { kind: "assignee" }
      | { kind: "supervisor" }
      | { kind: "coSupervisor" }
      | { kind: "role"; role: ActivityReviewRoleValue }
      | { kind: "person"; personId: Id<"persons"> };
    reviewers: ReviewerSpecValue[];
    reviewRoles: ActivityReviewRoleValue[];
    reviewMode: ActivityReviewModeValue | null;
    reviewSummaryLabel: string;
    // The same, to be set inside a sentence: "the activity admins".
    reviewersPhrase: string | null;
    // A step for the co-supervisor, and the student has none recorded.
    coSupervisorMissing: boolean;
    viewerCanReview: boolean;
    status: ActivityTaskStatusValue;
    statusLabel: string;
    // Gated behind an earlier step (model §6 of the plan). Shown, not hidden:
    // someone planning their year should see what is coming.
    locked: boolean;
    blockedBy: Array<{ title: string; taskId: string }>;
    responseData: Record<string, FieldValue> | null;
    formSchema: { fields: FieldDefinition[] } | null;
    lastSavedAt: number | null;
    submittedAt: number | null;
    // Set by a revision request and cleared by a submission. It is what tells
    // a draft that came back from a REVIEWER apart from one the person took
    // back themselves -- the query has always sent it; the type did not say so.
    lastRevisionRequestedAt: number | null;
    // Which reviewer slots this viewer may sign for. Empty for everyone else.
    actionableReviewers: Array<{ spec: ReviewerSpecValue; label: string }>;
    reviews: Array<{
      _id: string;
      reviewerSpec: ReviewerSpecValue;
      reviewerCapacity: ReviewCapacityValue | null;
      decision: ActivityReviewDecisionValue;
      comment: string;
      // The answers to the task's review form, where it has one. Null on a
      // revision request and on every review of a task without one.
      responseData: Record<string, string | number | boolean> | null;
      createdAt: number;
    }>;
  }>;
  dependencies: Array<{
    _id: string;
    dependsOnTitle: string | null;
    isSatisfied: boolean;
  }>;
};

// Days are "YYYY-MM-DD" (convex/days.ts); timestamps stay numbers.
/**
 * Today, as the day strings in this module are written.
 *
 * Computed in the browser, which is right for a comparison that only
 * decides a colour: the server already decides everything that binds.
 */
export function localToday(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Vienna" });
}

export function formatDay(day: string | null) {
  if (day === null) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

export function formatDayTime(value: number | null) {
  if (value === null) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(value);
}

/**
 * The composer's values into what the mutation stores.
 *
 * Unanswered optional fields are dropped rather than stored as null: an
 * absent key reads as "not answered", where a null would have to be told
 * apart from a deliberate blank.
 */
export function toResponseData(values: Record<string, ReviewFieldValue>) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) =>
      value !== null && value !== undefined && String(value) !== ""),
  ) as Record<string, string | number | boolean>;
}

/**
 * Activity names inside a sentence, as links where there is somewhere to go.
 *
 * A link is emphasis enough — quoting or italicising as well would be saying
 * the same thing twice. A predecessor the person does not hold has no page,
 * so it is named without one rather than given a dead link.
 */
export function ActivityRefs({
  items,
}: {
  items: Array<{ title: string; personActivityId: string | null }>;
}) {
  return (
    <>
      {items.map((item, index) => (
        <Fragment key={`${item.title}-${index}`}>
          {index > 0 ? " and " : ""}
          {item.personActivityId === null ? (
            <Text as="span" fontWeight="medium">{item.title}</Text>
          ) : (
            <TextLink>
              <Link to="/activities/$personActivityId" params={{ personActivityId: item.personActivityId }}>
                {item.title}
              </Link>
            </TextLink>
          )}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Step names inside a sentence, linked to the step itself.
 *
 * The step being waited for is on this same page, so the link scrolls to it
 * rather than navigating: sending somebody to a new page to read a row they
 * could have been shown in place is a worse answer than moving the page.
 */
export function TaskRefs({ items }: { items: Array<{ title: string; taskId: string }> }) {
  return (
    <>
      {items.map((item, index) => (
        <Fragment key={item.taskId}>
          {index > 0 ? " and " : ""}
          <TextLink>
            <a
              href={`#task-${item.taskId}`}
              onClick={(event) => {
                event.preventDefault();
                document.getElementById(`task-${item.taskId}`)?.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }}
            >
              {item.title}
            </a>
          </TextLink>
        </Fragment>
      ))}
    </>
  );
}

/**
 * Whether this step is waiting on the viewer: theirs to fill in, or theirs to
 * sign. Only what can be done now -- a locked step, or work already handed
 * on, is not waiting on anybody who is looking at it.
 */
export function stepWaitsOnViewer(task: AssignmentTask): boolean {
  if (task.locked) {
    return false;
  }
  if (task.status === "awaiting_review") {
    return task.actionableReviewers.length > 0;
  }
  return task.status !== "approved" && task.viewerCanPerform;
}

export function isFileValue(value: FieldValue): value is FileResponseValue {
  return value !== null && typeof value === "object" && !Array.isArray(value) && "key" in value;
}

// Normalizes single and multiple file fields to one list for rendering.
export function asFileList(value: FieldValue): FileResponseValue[] {
  if (Array.isArray(value)) {
    return value;
  }
  return isFileValue(value) ? [value] : [];
}

/**
 * Upload one file, asking for a fresh link on every attempt.
 *
 * The first attempt fails reliably on some networks -- a bare "NetworkError",
 * no response at all -- and clicking Save again works. The earlier retry here
 * did not help, and the reason is the one way a manual retry differs from an
 * automatic one: clicking Save mints a NEW signed URL, while the retry reused
 * the one that had just failed. So the retry now repeats the whole operation,
 * link and all, which is what the person was doing by hand.
 *
 * The waits are seconds rather than milliseconds for the same reason. Whatever
 * is cold on that first contact -- a proxy, a name to resolve, a handshake --
 * is not warm again four hundred milliseconds later; the successful manual
 * attempt came after a pause of that order.
 *
 * Only failures to CONNECT are retried. A refusal that arrived as a response
 * is an answer, and asking again gets the same one.
 */
export async function uploadWithRetry(
  file: File,
  mintUrl: () => Promise<{ key: string; url: string }>,
  attempts = 3,
): Promise<string> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const { key, url } = await mintUrl();
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed for ${file.name} (${response.status})`);
      }
      return key;
    } catch (error) {
      lastError = error;
      if (error instanceof Error && error.message.startsWith("Upload failed")) {
        throw error;
      }
      if (attempt < attempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 1500));
      }
    }
  }
  // Named for the reader, with the original kept as the cause so whoever
  // has to diagnose it still has the browser's own words.
  throw new Error(
    `${file.name} could not be uploaded because the connection failed. Please try again.`,
    { cause: lastError },
  );
}

export function fileIdentity(file: FileResponseValue): string {
  return file.key;
}

// File upload/list UI for one task form field. Uploaded files are shown from the
// stored values (name, size, download link, remove); the Chakra FileUpload list
// covers the in-flight selection, since already-stored files have no local File
// object to render. A single-file field hides the picker until the current file
// is removed.
export function TaskFileField({ field, taskId, files, pending, saving, readOnly = false, flash, onPendingChange, onRemove }: {
  field: FieldDefinition;
  taskId: Id<"personActivityTasks">;
  files: FileResponseValue[];
  pending: File[];
  saving: boolean;
  /** Finished or closed: the files stay readable, the controls go. */
  readOnly?: boolean;
  flash: string | null;
  onPendingChange: (files: File[]) => void;
  onRemove: (files: FileResponseValue[], target: FileResponseValue) => void;
}) {
  const taskFileUrl = useAction(api.azureFiles.taskFileUrl);
  // Minting a download link is a read, and reads follow impersonation.
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const [opening, setOpening] = useState<string | null>(null);

  // The link is minted on the click, after the server has checked that this
  // viewer may have this file. The blank tab is opened FIRST, while the click
  // is still trusted: opening one after an await is what a popup blocker
  // stops.
  //
  // Without `noopener`, deliberately. `window.open` returns NULL when it is
  // asked for one, and the whole point here is to keep the handle and send it
  // somewhere once the URL exists -- asking for both left a blank tab sitting
  // there with nothing to navigate it. The opener is cut afterwards instead,
  // which is the same protection a moment later.
  async function openFile(file: FileResponseValue) {
    const tab = window.open("", "_blank");
    setOpening(file.key);
    try {
      const url = await taskFileUrl({ taskId, key: file.key, effectivePersonId });
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        // Blocked by the browser: better the current tab than nothing.
        window.location.href = url;
      }
    } catch (error) {
      tab?.close();
      // The sentence first; the browser's own words second, where they
      // add anything. On their own they tell the reader nothing.
      const readable = readableError(error, "The file could not be opened");
      toastError(readable.message, readable.detail);
    } finally {
      setOpening(null);
    }
  }

  const multiple = Boolean(field.multiple);
  // A single-file field takes no further pick once it holds something: replacing a
  // file is an explicit remove first. Only the trigger is withheld -- the picker
  // itself must stay mounted, because FileUpload.List is what shows the files
  // waiting to be uploaded.
  const atCapacity = !multiple && (files.length > 0 || pending.length > 0);

  return (
    <Stack gap={3}>
      <FileUploadRoot
        maxFiles={multiple ? 20 : 1}
        acceptedFiles={pending}
        onFileChange={(details) => onPendingChange(details.acceptedFiles)}
      >
        <FileUploadHiddenInput />
        {atCapacity ? null : (
          <FileUploadTrigger asChild>
            <Button size="sm" variant="outline" alignSelf="flex-start" disabled={saving || readOnly}>
              <LuUpload />
              {multiple ? "Add files" : "Choose file"}
            </Button>
          </FileUploadTrigger>
        )}
        <FileUploadList showSize clearable />
      </FileUploadRoot>

      {!multiple && files.length > 0 ? (
        <Text fontSize="xs" color="fg.muted">
          Remove the current file to upload a different one.
        </Text>
      ) : null}

      {pending.length > 0 ? (
        <Text fontSize="xs" color="fg.muted">
          {saving
            ? "Uploading…"
            : `${pending.length === 1 ? "This file" : `These ${pending.length} files`} will be uploaded when you save or submit.`}
        </Text>
      ) : null}
      {flash ? <Text fontSize="xs" color="fg.muted">{flash}</Text> : null}

      {files.map((file) => (
        <Box key={fileIdentity(file)} borderWidth="1px" borderColor="border" px={4} py={3}>
          <HStack justify="space-between" align="flex-start" gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="medium">{file.fileName}</Text>
              <Text mt={1} fontSize="sm" color="fg.muted">{formatFileSize(file.size)}</Text>
            </Box>
            <IconButton
              aria-label={`Remove ${file.fileName}`}
              size="xs"
              variant="ghost"
              color="fg.muted"
              disabled={saving || readOnly}
              onClick={() => onRemove(files, file)}
            >
              <LuTrash2 />
            </IconButton>
          </HStack>
          <Button
            mt={3}
            size="xs"
            variant="ghost"
            colorPalette="blue"
            loading={opening === file.key}
            onClick={() => void openFile(file)}
          >
            <LuFileDown />
            Open file
          </Button>
        </Box>
      ))}
    </Stack>
  );
}

export function formatFileSize(size: number | null): string {
  if (!size) {
    return "Uploaded file";
  }
  return size >= 1024 * 1024
    ? `${(size / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(size / 1024))} KB`;
}

// What the server accepts in responseData: the same values without `url`.
export type StoredFieldValue =
  | string
  | number
  | boolean
  | BaseFileResponseValue
  | BaseFileResponseValue[]
  | null;

// The server adds `url` when hydrating a task, but the stored value must not
// contain it -- responseData is validated against an exact object shape.
export function stripDownloadUrl(value: FieldValue): StoredFieldValue {
  if (Array.isArray(value)) {
    return value.map(({ url: _url, ...rest }) => rest);
  }
  if (isFileValue(value)) {
    const { url: _url, ...rest } = value;
    return rest;
  }
  return value;
}

export function TagBadge({ name, color, title }: { name: string; color: string | null; title: string }) {
  return (
    <Badge
      variant="outline"
      textTransform="uppercase"
      letterSpacing="0.18em"
      style={{ borderColor: tagColor(color), color: tagColor(color) }}
      title={title}
    >
      {name}
    </Badge>
  );
}
